import React, { useState } from 'react';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface SegmentedCircleProps {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    segments?: number;
    bgColor?: string;
    textColor?: string;
    fontSizeRatio?: number;
    tickLength?: number;
}

export const SegmentedCircle: React.FC<SegmentedCircleProps> = ({
    percentage,
    size = 50,
    strokeWidth = 2,
    segments = 36,
    bgColor = '#e0e0e0',
    textColor = '#222',
    fontSizeRatio,
    tickLength,
}) => {
    const { themeStyle } = useThemeContext();
    const color = theme[themeStyle].highlight;

    const [hovered, setHovered] = useState(false);

    // Interpolate between base and expanded
    const baseSize = size;
    const expandedSize = size * 1.25;
    const currentSize = hovered ? expandedSize : baseSize;

    const radius = currentSize / 2;
    const angleStep = 360 / segments;
    const filledSegments = Math.round((percentage / 100) * segments);

    const effectiveTickLength = (tickLength ?? baseSize * 0.12) * (hovered ? 1.3 : 1);
    const effectiveFontSize = currentSize * (fontSizeRatio ?? 0.3);

    const getTick = (index: number) => {
        const angle = (index * angleStep - 90) * (Math.PI / 180);
        const x1 = radius + (radius - effectiveTickLength) * Math.cos(angle);
        const y1 = radius + (radius - effectiveTickLength) * Math.sin(angle);
        const x2 = radius + radius * Math.cos(angle);
        const y2 = radius + radius * Math.sin(angle);
        return { x1, y1, x2, y2 };
    };

    return (
        <svg
            width={currentSize}
            height={currentSize}
            style={{ display: 'block', transition: 'width 0.2s ease, height 0.2s ease' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}>
            {[...Array(segments)].map((_, i) => {
                const { x1, y1, x2, y2 } = getTick(i);
                return (
                    <line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={i < filledSegments ? color : bgColor}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        style={{ transition: 'stroke 0.2s ease' }}
                    />
                );
            })}
            <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dy=".3em"
                fontSize={effectiveFontSize}
                fontWeight="bold"
                fill={textColor}
                style={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    color: theme[themeStyle].tag_white,
                    transition: 'font-size 0.2s ease',
                }}>
                {`${Math.round(percentage)}%`}
            </text>
        </svg>
    );
};
